import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Input } from '../forms/Input';
import { Select, SelectOption } from '../forms/Select';
import { Button } from '../Button';
import { ContactFormData } from '../../types/contact';
import type { EmailFunctionResponse, EmailErrorResponse } from '../../types/email';

/**
 * Subject options for contact form
 */
const SUBJECT_OPTIONS: SelectOption[] = [
  { value: 'general-question', label: 'General Question' },
  { value: 'course-information', label: 'Course Information' },
  { value: 'group-training-request', label: 'Group Training Request' },
  { value: 'renewal-question', label: 'Renewal Question' },
  { value: 'other', label: 'Other' },
];

/**
 * Maximum character count for message field
 */
const MAX_MESSAGE_LENGTH = 1000;

/**
 * ContactForm Component
 *
 * Handles customer inquiries with validation for:
 * - Full name (required, min 2 chars)
 * - Email (required, valid email pattern)
 * - Phone (optional, phone pattern if provided)
 * - Subject (required, dropdown selection)
 * - Message (required, min 10 chars, max 1000 chars)
 */
export const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [messageLength, setMessageLength] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      subject: 'general-question',
      message: '',
    },
  });

  // Watch message field for character counter
  const messageValue = watch('message');

  useEffect(() => {
    setMessageLength(messageValue?.length || 0);
  }, [messageValue]);

  /**
   * Handle form submission
   */
  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Call Netlify Function to send emails
      const response = await fetch('/.netlify/functions/send-contact-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      const result: EmailFunctionResponse | EmailErrorResponse = await response.json();

      if (!response.ok || !result.success) {
        const errorMsg = 'error' in result ? result.error : 'Failed to send message';
        throw new Error(errorMsg);
      }

      // SUCCESS: Show message, reset form
      console.log('Contact emails sent:', result);
      setSubmitSuccess(true);
      reset();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'We\'re having trouble processing your message. Please try again or call us directly.'
      );

      // Auto-hide error after 10 seconds
      setTimeout(() => setSubmitError(null), 10000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-white p-6 md:p-8 border-4 border-rich_black-500 shadow-[8px_8px_0px_0px_rgba(2,5,18,1)]" data-aos="fade-up">
      <h2 className="font-display text-2xl md:text-3xl uppercase tracking-wider font-bold text-indigo_dye-500 mb-6">Contact Us</h2>

      {submitSuccess && (
        <div className="mb-6 p-4 bg-green-100 border-3 border-green-600 animate-slideInFromTop">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="font-mono text-sm uppercase font-semibold text-green-800">
              Thank you for contacting us! We'll get back to you as soon as possible.
            </p>
          </div>
        </div>
      )}

      {/* ERROR MESSAGE: Show if submission fails */}
      {submitError && (
        <div className="mb-6 p-4 bg-red-100 border-3 border-red-600">
          <div className="flex items-start gap-2">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-mono text-sm uppercase font-semibold text-red-800 mb-2">{submitError}</p>
              <p className="font-mono text-xs text-red-700">
                Please try again, or visit our <a href="/contact" className="underline font-semibold">contact page</a>.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Full Name */}
        <Input
          name="fullName"
          label="Full Name"
          type="text"
          placeholder="John Doe"
          required
          error={errors.fullName?.message}
          register={register('fullName', {
            required: 'Full name is required',
            minLength: {
              value: 2,
              message: 'Full name must be at least 2 characters',
            },
          })}
        />

        {/* Email */}
        <Input
          name="email"
          label="Email Address"
          type="email"
          placeholder="john@example.com"
          required
          error={errors.email?.message}
          register={register('email', {
            required: 'Email address is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: 'Please enter a valid email address',
            },
          })}
        />

        {/* Phone (Optional) */}
        <Input
          name="phone"
          label="Phone Number"
          type="tel"
          placeholder="(123) 456-7890 or 123-456-7890"
          error={errors.phone?.message}
          register={register('phone', {
            pattern: {
              value: /^[+]?[1]?[-\s.]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4}$/,
              message: 'Please enter a valid phone number',
            },
          })}
        />

        {/* Subject */}
        <Select
          name="subject"
          label="Subject"
          options={SUBJECT_OPTIONS}
          required
          error={errors.subject?.message}
          register={register('subject', {
            required: 'Please select a subject',
          })}
        />

        {/* Message */}
        <div className="w-full">
          <div className="flex items-center justify-between mb-2">
            <label
              htmlFor="message"
              className="block font-mono text-sm uppercase tracking-wide font-semibold text-gray-900"
            >
              Message
              <span className="text-amaranth_purple-500 ml-1">*</span>
            </label>
            <span
              className={`font-mono text-xs uppercase tracking-wide ${
                messageLength > MAX_MESSAGE_LENGTH
                  ? 'text-amaranth_purple-600 font-bold'
                  : 'text-gray-500 font-semibold'
              }`}
            >
              {MAX_MESSAGE_LENGTH - messageLength} chars
            </span>
          </div>
          <textarea
            id="message"
            rows={6}
            placeholder="Please describe your question or request..."
            aria-invalid={errors.message ? 'true' : 'false'}
            aria-describedby={errors.message ? 'message-error' : undefined}
            className={`block w-full px-4 py-3 text-gray-900 border-3 font-body transition-all duration-200 focus:outline-none focus:border-4 focus:-translate-y-1 focus:shadow-[4px_4px_0px_0px_rgba(0,60,102,0.3)] disabled:opacity-50 disabled:cursor-not-allowed resize-y bg-white ${
              errors.message
                ? 'border-amaranth_purple-500 focus:border-amaranth_purple-500'
                : 'border-gray-400 focus:border-indigo_dye-500 hover:bg-gray-50'
            }`}
            {...register('message', {
              required: 'Message is required',
              minLength: {
                value: 10,
                message: 'Message must be at least 10 characters',
              },
              maxLength: {
                value: MAX_MESSAGE_LENGTH,
                message: `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`,
              },
            })}
          />
          {errors.message && (
            <p
              id="message-error"
              className="mt-2 font-mono text-xs uppercase tracking-wide font-bold text-amaranth_purple-600"
              role="alert"
            >
              {errors.message.message}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="xl"
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
          </Button>
        </div>
      </form>
    </div>
  );
};
